<template>
  <w-sliding-popup ref="slidingPopup" title="로그 상세 정보" width="900px">
    <div slot="content">
      <div class="title-bar title-h4">로그 정보</div>
      <el-row class="mr-b25">
        <div class="list form-list">
          <el-form label-position="left">
            <el-form-item label-width="120px" v-for="info in logDetails" :label="getLogInfoLabel(info.key).fieldName" :key="`${info.key}_${info.value}`">
              {{ getLogInfoValue(info) }}
            </el-form-item>
            <el-form-item label="버전" label-width="120px" v-if="selectedLog.logType === 'TRAP'">{{getValueAfterCheckEmpty(selectedLog.snmpVersion)}}</el-form-item>
            <el-form-item label="Generic" label-width="120px" v-if="selectedLog.logType === 'TRAP'">{{getValueAfterCheckEmpty(snmptrapName)}}</el-form-item>
            <el-form-item label="Specific" label-width="120px" v-if="selectedLog.logType === 'TRAP'">{{getValueAfterCheckEmpty(messageDetail.specific)}}</el-form-item>
            <el-form-item label="상세 메시지" label-width="120px" v-if="selectedLog.logType === 'TRAP'">
              <div v-html="messageDetailText"></div>
            </el-form-item>
          </el-form>
        </div>
      </el-row>
      <div class="title-bar title-h4">장비 정보</div>
      <w-server-obj-info class="mr-b25" v-if="selectedLog.logType === 'APP'" :obj-info="objInfo"></w-server-obj-info>
      <w-netdevice-obj-info class="mr-b25" v-else :obj-info="objInfo"></w-netdevice-obj-info>
      <div class="title-bar title-h4">
        연관 로그
        <el-popover placement="bottom" width="500" trigger="hover" :offset="40" class="mr-l4">
          <span class="text"> - 선택한 로그를 기준으로 <em>앞,뒤 30초</em> 이내 발생한 로그 입니다.</span>
          <span slot="reference"><i class="fas fa-info-circle" style="cursor: pointer"></i></span>
        </el-popover>
      </div>
      <el-table-wrapper class="mr-b25" :default-column="columnDefault" :columns="columns" :data="relativeLogs" max-height="300" :pagination="false">
      </el-table-wrapper>
      <div class="title-bar title-h4">장비 로그 이력 차트</div>
      <div>
        <highcharts class="chart" :options="options"></highcharts>
      </div>
    </div>
  </w-sliding-popup>
</template>

<script>
import { isNil, isEmpty, cloneDeep, forEach, find } from 'lodash';
import moment from 'moment';
import { stringify } from 'querystring';

import LogAPI from '@/api/log/integrated-log';
import BasicLogAPI from '@/api/log/common';
import PerfNetAPI from '@/api/performance/netdevice/netdevice-info';
import PerfServerAPI from '@/api/performance/server/info-table';
import msgUtil from '@/utils/message';
import { isTrue } from '@/utils/boolean';
import { getValueAfterCheckEmpty, EMPTY_TEXT } from '@/utils/string';
import { convertValueByUnitData } from '@/utils/number';
import { getDateToString, getRequestDatetimeParams, getIntervalDatetimeParams } from '@/utils/date';
import { columnDefault } from '@/constants/table';
import { lineOptions } from '@/constants/chart-options';

import WNetdeviceObjInfo from '@/views/log-analysis/components/NetdeviceObjInfo';
import WServerObjInfo from '@/views/log-analysis/components/ServerObjInfo';

export default {
  name: 'WLogDetailPopup',
  components: {
    WServerObjInfo,
    WNetdeviceObjInfo,
  },
  data() {
    const columns = [
      {
        prop: 'logType',
        label: '로그 종류',
        width: 90,
        formatter: this.getStringFormatter,
        sortable: true,
      },
      {
        prop: 'sourceName',
        label: '로그 소스',
        formatter: this.getStringFormatter,
        sortable: true,
        width: '100',
      },
      {
        prop: 'originMessage',
        label: '원본 메시지',
        formatter: this.getStringFormatter,
        sortable: true,
        scopedSlot: 'event-message',
        showOverflowTooltip: true,
      },
      {
        prop: 'timestamp',
        label: '수집 시간',
        formatter: this.getDateFormatter,
        sortable: true,
        width: '150',
      },
    ];
    return {
      subOidList: [],
      fields: [],
      mlPatterns: [],
      options: cloneDeep(lineOptions),
      objInfo: {},
      columns,
      relativeLogs: [],
      columnDefault,
      selectedLog: {},
      logDetails: [],
      messageDetail: {},
      messageDetailText: null,
      snmptrapName: null,
    };
  },
  watch: {
    selectedLog: {
      immediate: true,
      handler(value) {
        this.logDetails = [];
        if (!isEmpty(value)) {
          LogAPI.getMlPatternWithName().then((response) => {
            if (response.data.success) {
              this.mlPatterns = response.data.data;
            }
          }).catch((e) => {
            this.$message(msgUtil.getResultMsgData(msgUtil.action.VIEW, '패턴', false));
            throw new Error(e);
          });
          LogAPI.getLogAllFields().then((response) => {
            if (response.data.success) {
              this.fields = response.data.data;
              if (!isEmpty(value.messageDetail) && value.logType === 'TRAP') {
                if ((typeof value.messageDetail) === 'string') {
                  this.messageDetail = JSON.parse(value.messageDetail);
                } else {
                  this.messageDetail = value.messageDetail;
                }
              }
              this.loadChartData(value);
              this.loadLogInfo(value);
              this.loadRelativeLogs(value);
              this.loadObjInfo(value);
            }
          }).catch((e) => {
            this.$message(msgUtil.getResultMsgData(msgUtil.action.VIEW, '필드', false));
            throw new Error(e);
          });
        }
      },
    },
  },
  methods: {
    /**
     * 장비의 로그 이력 차트 데이터를 조회해온다. (param : objId, logType)
     * @author Minyeong
     * @return {[type]} [description]
     */
    loadChartData(log) {
      const timestamp = getIntervalDatetimeParams(log.timestamp, 'hour', 9);
      const endDateTime = getRequestDatetimeParams(timestamp);
      const startDateTime = getIntervalDatetimeParams(timestamp, 'hour', 24);
      const params = {
        objId: log.objId,
        objName: log.objName,
        logType: log.logType,
        startDateTime,
        endDateTime,
      };
      LogAPI.getLogHistChartData(params).then((response) => {
        if (response.data.success) {
          this.options = cloneDeep(lineOptions);
          this.options.tooltip = {
            xDateFormat: '%Y-%m-%d %H:%M:%S',
            split: false,
            shared: true,
            valueDecimals: 0,
            valueSuffix: '개',
            formatter() {
              let tooltip = getDateToString(this.x, 'YYYY-MM-DD HH:mm:ss');
              this.points.forEach((point) => {
                const yValue = convertValueByUnitData(point.y, 'count', '개', 1, null, null, true);
                tooltip += `<br/><span style="color:${point.color}">\u25CF</span> ${point.series.name}: <b>${yValue}</b><br/>`;
              });
              return tooltip;
            },
          };
          this.options.series = [{ name: '수집 로그 수', data: response.data.data }];
          this.options.chart.height = 200;
        }
      }).catch((e) => {
        this.$message(msgUtil.getResultMsgData(msgUtil.action.VIEW, '차트 데이터', false));
        throw new Error(e);
      });
    },
    /**
     * 팝업에 보여줄 로그 데이터(selectedLog)를 key/value 데이터로 가공한다.
     * @author Minyeong
     * @param  {[type]} log [description]
     * @return {[type]}     [description]
     */
    loadLogInfo(log) {
      const keys = Object.keys(log);
      const logInfos = [];
      const regex = /(^\W|_)/;

      forEach(keys, (item) => {
        if (!regex.test(item)) {
          if (!(log.logType === 'TRAP' && item === 'messageDetail')) {
            const info = { key: item, value: log[item] };
            const field = this.getLogInfoLabel(item);
            let fieldName = null;
            if (!isEmpty(field)) {
              fieldName = field.fieldName;
            }
            if (!isNil(fieldName) && (item.indexOf('Id') === -1 || field.defaultFlag !== 'Y')) {
              logInfos.push(info);
            }
            if (!isNil(fieldName) && item === 'mlPatternId' && log[item] !== 0) {
              const mlPattern = find(this.mlPatterns, (pattern) => pattern.mlPatternId === log[item]);
              if (!isEmpty(mlPattern)) {
                logInfos.push({ key: item, value: mlPattern.mlPatternName });
              } else {
                logInfos.push({ key: 'mlPatternId', value: EMPTY_TEXT });
              }
            }
          } else if (!isEmpty(this.selectedLog.messageDetail)) {
            if ((typeof this.selectedLog.messageDetail) === 'string') {
              this.messageDetail = JSON.parse(this.selectedLog.messageDetail);
            } else {
              this.messageDetail = this.selectedLog.messageDetail;
            }
          }
        }
      });

      if (log.mlPatternId === 0) {
        logInfos.push({ key: 'mlPatternId', value: EMPTY_TEXT });
      }
      this.logDetails = logInfos;
    },
    loadRelativeLogs(log) {
      // 2. 선택된 로그의 수집 시각 기준으로 startDatetime/endDatetime설정해서 로그 조회
      const timestamp = getIntervalDatetimeParams(log.timestamp, 'hour', 9);
      const endDateTime = getIntervalDatetimeParams(timestamp, 'minute', -1);
      const startDateTime = getIntervalDatetimeParams(timestamp, 'minute', 1);

      const params = {
        type: 'root',
        start: 0,
        limit: 100,
        startDateTime,
        endDateTime,
      };
      LogAPI.getLogDatas(params).then((response) => {
        if (response.data.success) {
          this.relativeLogs = response.data.data;
        }
      }).catch((e) => {
        this.$message(msgUtil.getResultMsgData(msgUtil.action.VIEW, '로그', false));
        throw new Error(e);
      });
    },
    /**
     * objId로 서버/네트워크 장비의 상세정보 데이터를 가져온다.
     * @author Minyeong
     * @return {[type]} [description]
     */
    loadObjInfo(log) {
      // TODO 서버인지 네트워크 장비인지 구분할 수 있는 기준값 필요(현재는 로그타입으로 구분)
      const params = {
        objId: log.objId,
      };
      if (log.logType === 'APP') {
        // APPLOG일 때 > 서버 장비
        PerfServerAPI.getServerConfigurationInfo(params).then((response) => {
          if (response.data.success && !isEmpty(response.data.data)) {
            const configInfo = response.data.data;
            configInfo.majorLabel = !isEmpty(configInfo.majorName) ? `[${configInfo.majorDepartment}]${configInfo.majorName}` : '-'; // TODO: String util의 getValueAfterCheckEmpty function 활용 (utils/string > getValueAfterCheckEmpty)
            configInfo.minorLabel = !isEmpty(configInfo.minorName) ? `[${configInfo.minorDepartment}]${configInfo.minorName}` : '-';
            configInfo.checkDatetimeLabel = !isEmpty(configInfo.checkDatetime) ? moment(configInfo.checkDatetime).format('YYYY년 MM월 DD일 HH:mm:ss') : '-';
            this.objInfo = configInfo;
          }
        }).catch((e) => {
          this.$message(msgUtil.getResultMsgData(msgUtil.action.VIEW, '장비 정보', false));
          throw new Error(e);
        });
      } else {
        // SYSLOG일 때 > 네트워크 장비
        PerfNetAPI.getNetdeviceConfigurationInfo(params).then((response) => {
          if (response.data.success && !isEmpty(response.data.data)) {
            const configInfo = response.data.data;
            configInfo.majorLabel = !isEmpty(configInfo.majorName) ? `[${configInfo.majorDepartment}]${configInfo.majorName}` : '-'; // TODO: String util의 getValueAfterCheckEmpty function 활용 (utils/string > getValueAfterCheckEmpty)
            configInfo.minorLabel = !isEmpty(configInfo.minorName) ? `[${configInfo.minorDepartment}]${configInfo.minorName}` : '-';
            configInfo.checkDatetimeLabel = !isEmpty(configInfo.checkDatetime) ? moment(configInfo.checkDatetime).format('YYYY년 MM월 DD일 HH:mm:ss') : '-';
            configInfo.netdeviceUptimeLabel = !isNil(configInfo.netdeviceUptimeCount) ? `${configInfo.netdeviceUptimeCount}일` : EMPTY_TEXT;
            // configInfo.netdeviceUptimeLabel = calcUptime(new Date(configInfo.netdeviceUptime), new Date());
            this.objInfo = configInfo;
          }
        }).catch((e) => {
          this.$message(msgUtil.getResultMsgData(msgUtil.action.VIEW, '장비 정보', false));
          throw new Error(e);
        });
        if (this.selectedLog.logType === 'TRAP' && !isEmpty(this.selectedLog.messageDetail) && !isEmpty(this.messageDetail.generic)) {
          //  snmpTrap일 때 > snmpName
          const queryString = stringify({ generic: this.messageDetail.generic });
          BasicLogAPI.getSnmpTrapName(queryString).then((response) => {
            if (response.data.success) {
              this.snmptrapName = response.data.data;
            }
          }).catch((e) => {
            this.$message(msgUtil.getResultMsgData(msgUtil.action.VIEW, '트랩 정보', false));
            throw new Error(e);
          });
        }
      }
    },
    getSubOIDList() {
      const params = {
        snmptrapOid: this.selectedLog.originMessage,
        snmptrapVersion: this.selectedLog.snmpVersion,
      };
      LogAPI.getSubOIDList(params).then((response) => {
        if (response.data.success) {
          this.subOidList = response.data.data;
          this.getMessageDetail();
        }
      }).catch((e) => {
        this.$message(msgUtil.getResultMsgData(msgUtil.action.VIEW, 'Sub Oid', false));
        throw new Error(e);
      });
    },
    open(log) {
      this.messageDetailText = '';
      this.selectedLog = log;
      if (log.logType === 'TRAP') {
        this.getSubOIDList();
      }
      this.messageDetail = '';
      this.$refs.slidingPopup.open();
    },
    getValueAfterCheckEmpty,
    /**
     * 로그 상세정보 폼의 라벨(fieldName)을 반환한다.
     * @author Minyeong
     * @param  {[type]} value [description]
     * @return {[type]}       [description]
     */
    getLogInfoLabel(value) {
      const field = find(this.fields, (item) => item.fieldId === value);
      if (!isEmpty(field)) {
        return field;
      }
      return null;
    },
    /**
     * 로그 상세정보 폼의 값(value)를 반환한다. (확인 여부/timestamp check)
     * @author Minyeong
     * @param  {[type]} info [description]
     * @return {[type]}      [description]
     */
    getLogInfoValue(info) {
      const key = info.key;
      const value = info.value;

      if (key === 'isChecked' && isTrue(value)) {
        return '확인';
      } else if (key === 'isChecked' && !isTrue(value)) {
        return '미확인';
      }

      if (key === 'timestamp') {
        return this.getDateFormatter(null, null, value);
      }

      return getValueAfterCheckEmpty(value);
    },
    getMessageDetail() {
      const message = this.selectedLog.messageDetail;
      let messageDetail = '';
      if (!isEmpty(message)) {
        message.forEach((item, index) => {
          if (!isEmpty(this.subOidList)) {
            const subOid = this.subOidList.find((trap) => `.${trap.snmpOid}` === item.snmpOid);
            messageDetail += `&nbsp;&nbsp;&nbsp;정책명 : ${(isNil(subOid)) ? EMPTY_TEXT : subOid.oidName}<br/>`;
          } else {
            messageDetail += `&nbsp;&nbsp;&nbsp;정책명 : ${EMPTY_TEXT}<br/>`;
          }
          messageDetail += `&nbsp;&nbsp;&nbsp;snmpOid : ${item.snmpOid}<br/>`;
          messageDetail += `&nbsp;&nbsp;&nbsp;snmpType : ${item.snmpType}<br/>`;
          messageDetail += `&nbsp;&nbsp;&nbsp;vIndex : ${item.vIndex}<br/>`;
          messageDetail += `&nbsp;&nbsp;&nbsp;value : ${item.value}<br/>`;
          if (message.length > 2 && message.length !== index + 1) {
            messageDetail += '<hr class="mr-t10 mr-b10" />';
          }
        });
      } else {
        this.messageDetailText = EMPTY_TEXT;
      }
      this.messageDetailText = messageDetail;
    },
    getDateFormatter(row, column, value) {
      if (!isNil(value)) {
        return getDateToString(getIntervalDatetimeParams(value, 'hour', 9), 'YYYY-MM-DD HH:mm:ss');
      }
      return EMPTY_TEXT;
    },
    getStringFormatter(row, column, value) {
      if (!isNil(value)) {
        return value;
      }
      return EMPTY_TEXT;
    },
  },
};
</script>

<style>

</style>



// WEBPACK FOOTER //
// src/views/log-analysis/popup/LogDetailPopup.vue